import _ from 'underscore';
import Promise from 'bluebird';
import kendo from '@bingads-webui/kendo/kendo.core';
import { downloadify } from '@bingads-webui/download-service';
import { segmentationType } from '@bingads-webui-campaign/segmentation-utilities';

import { AUCTION_INSIGHT_COLUMN_METRICS, DEVICE_SEGMENT_TYPES } from './consts';

const formatDate = date => kendo.toString(date, 'yyyyMMdd');
const formatHeaderDate = date => kendo.toString(date, 'MMMM dd, yyyy');
const formatCellDate = date => kendo.toString(date, 'MM/dd/yyyy');

// Always use English in header
const headerMap = {
  DayOfWeek: 'Day of the week',
  HourOfDay: 'Hour of day',
  DisplayDomain: 'Display URL domain',
  DisplayDomainShopping: 'Store display name',
  [AUCTION_INSIGHT_COLUMN_METRICS.ImpressionShare]: 'Impression share',
  [AUCTION_INSIGHT_COLUMN_METRICS.AveragePosition]: 'Avg. position',
  [AUCTION_INSIGHT_COLUMN_METRICS.OverlapRate]: 'Overlap rate',
  [AUCTION_INSIGHT_COLUMN_METRICS.AboveRate]: 'Position above rate',
  [AUCTION_INSIGHT_COLUMN_METRICS.TopOfPageRate]: 'Top of page rate',
  [AUCTION_INSIGHT_COLUMN_METRICS.AbsoluteTopOfPageRate]: 'Abs. top of page rate',
  [AUCTION_INSIGHT_COLUMN_METRICS.OutrankingShare]: 'Outranking share',
};

/**
 * Convert data to csv format
 * @param {Array} data - Should be a list of lists, where each sub-list is a row.
 * @returns {string} - a string with comma delimiters
 */
function data2Csv(data) {
  let csvString = '';
  _.each(data, (row) => {
    const rowString = row.join(',');
    csvString += `${rowString}\r\n`;
  });

  return csvString;
}

function formatPercent(value) {
  const valueInPercent = `${parseFloat(value * 100).toFixed(2)}%`;
  return valueInPercent;
}

export function handleCustomSegmentation(finalParams, queryParams) {
  const finalParameters = finalParams;
  if (!_.isEmpty(queryParams)) {
    _.each(_.keys(queryParams), (key) => {
      if (key === 'SegmentationTypes' && queryParams[key] !== undefined) {
        if (key in finalParameters.query) {
          // the case that segmentation already exists and we update it.
          /* eslint-disable-next-line no-param-reassign */
          finalParameters.query[key] = queryParams[key];
        } else {
          // the case that segmentation starts off as none and we are adding it
          finalParameters.query = _.extend(queryParams, finalParams.query);
        }
      } else { // the case where we start off with segmentation but we update it to none
        finalParameters.query = _.omit(finalParams.query, key);
      }
    });
  }
  return finalParameters;
}

export function formatData(data) {
  /* eslint-disable no-param-reassign */
  const formattedData = JSON.parse(JSON.stringify(data)); // deep clone
  _.each(formattedData, (competitor) => {
    _.each(_.keys(competitor.PerformanceMetrics), (metric) => {
      switch (metric) {
        case AUCTION_INSIGHT_COLUMN_METRICS.ImpressionShare:
        case AUCTION_INSIGHT_COLUMN_METRICS.TopOfPageRate:
        case AUCTION_INSIGHT_COLUMN_METRICS.AbsoluteTopOfPageRate: {
          competitor.PerformanceMetrics[metric] =
            `${formatPercent(competitor.PerformanceMetrics[metric])}`;
          break;
        }
        case AUCTION_INSIGHT_COLUMN_METRICS.AboveRate:
        case AUCTION_INSIGHT_COLUMN_METRICS.OverlapRate:
        case AUCTION_INSIGHT_COLUMN_METRICS.OutrankingShare: {
          competitor.PerformanceMetrics[metric] =
            `${formatPercent(competitor.PerformanceMetrics[metric])}`;
          if (competitor.DisplayDomain === 'You') {
            competitor.PerformanceMetrics[metric] = '--';
          }
          break;
        }
        case AUCTION_INSIGHT_COLUMN_METRICS.SegmentedData: {
          _.each(competitor.PerformanceMetrics[metric], (segment) => {
            if (segment.Key[0].SegmentationType === segmentationType.Device
              && segment.Key[0].StringValue === DEVICE_SEGMENT_TYPES.Computer) {
              segment.Key[0].StringValue = 'Computer';
            } else if (segment.Key[0].SegmentationType === segmentationType.Device
              && segment.Key[0].StringValue === DEVICE_SEGMENT_TYPES.SmartPhone) {
              segment.Key[0].StringValue = 'Smartphone';
            } else if (segment.Key[0].SegmentationType === segmentationType.Week) {
              segment.Key[0].StringValue = `${formatCellDate(segment.Key[0].StringValue)}`;
            }
            _.each(_.keys(segment.Data), (SegMetric) => {
              if (SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.ImpressionShare ||
                SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.AboveRate ||
                SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.OverlapRate ||
                SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.OutrankingShare ||
                SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.TopOfPageRate ||
                SegMetric === AUCTION_INSIGHT_COLUMN_METRICS.AbsoluteTopOfPageRate) {
                segment.Data[SegMetric] =
                  `${formatPercent(segment.Data[SegMetric])}`;
              }
            });
          });
          break;
        }
        default:
          break;
      }
    });
  });
  return formattedData;
}

/**
 * Localized column header.
 * For example. DisplayDomain -> Display URL domain
 * @param {Array} header - header name
 * @param {string} campaignType - search campaign or shopping campaign
 * @returns {string} localized header string
 */
function localizeHeader(header, campaignType = null) {
  if (header === 'DisplayDomain') {
    return campaignType === 'Shopping' ? headerMap.DisplayDomainShopping : headerMap.DisplayDomain;
  }

  return _.result(headerMap, header, header);
}

function localizeHeaders(columns, campaignType, segmentType) {
  const headers = [];

  if (segmentType != null) {
    headers.push(localizeHeader(segmentType));
  }

  _.each(columns, (column) => {
    headers.push(localizeHeader(column, campaignType));
  });

  return Promise.resolve(headers);
}

export function processDataIntoArrays(data, segmentType, selectedColumns, i18n, campaignType) {
  const csvArray = []; // the array to hold data that we will turn into a CSV

  return localizeHeaders(selectedColumns, campaignType, segmentType)
    .then((localizedColumns) => {
      csvArray.push(localizedColumns); // header row
    }).then(() => {
      const formattedData = formatData(data.items);

      /* Parse data from gridView query and populate arrays. */
      if (segmentType != null) {
        _.each(formattedData, (competitor) => {
          const displayDomain = competitor.DisplayDomain;
          _.each(competitor.PerformanceMetrics.SegmentedData, (category) => {
            const row = [];
            row.push(category.Key[0].StringValue); // segment
            row.push(displayDomain); // display domain
            row.push(_.values(_.pick(category.Data, selectedColumns))); // metrics
            csvArray.push(_.flatten(row));
          });
        });
      } else {
        _.each(formattedData, (competitor) => {
          const row = [];
          row.push(competitor.DisplayDomain); // display domain
          row.push(_.values(_.pick(competitor.PerformanceMetrics, selectedColumns))); // metrics
          csvArray.push(_.flatten(row));
        });
      }

      return csvArray;
    });
}

/**
 * Do clientside auction insight data download
 * @param {string} format - download file format. e.g csv
 * @param {string} segmentType - segmentation type. e.g Device, HourOfDay
 * @param {Object} gridView - gridView object
 * @param {Object} scope - Current Account, Campaign, Adgroup information
 * @param {Object} state - state that contains currently selected time rage
 * @param {Object} i18n - localization object
 * @param {Function} showAlertHandler - show alert information
 * @returns {void}
 */
export function clientSideDownload(
  format,
  segmentType,
  gridView,
  scope,
  state,
  i18n,
  showAlertHandler
) {
  showAlertHandler({
    message: i18n.getString('GridDownloadStatusMessage'),
    level: 'Info',
    dismissible: true,
  });

  /* get columns from preferences */
  if (format && gridView && scope && state) {
    const selectedColumns = _.pluck(gridView.grid.gridView.get('columns'), 'name');

    gridView.grid.dataSource.query({ SegmentationTypes: segmentType }, {
      queryTransform: (finalParams, queryParams) =>
        handleCustomSegmentation(finalParams, queryParams),
    }).then(data => processDataIntoArrays(
      data,
      segmentType,
      selectedColumns,
      i18n,
      gridView.options.campaignType
    )).then((csvArray) => {
      /* file download */
      state.getPromise('$daterange').then((dateRange) => {
        const StartDate = formatDate(dateRange.start);
        const EndDate = formatDate(dateRange.end);
        const filename = `AuctionInsights_Report_${scope.accountId}_${StartDate}_${EndDate}.${format}`;
        const header = `AuctionInsights_Report\n"${formatHeaderDate(dateRange.start)} - ${formatHeaderDate(dateRange.end)}"\n`;

        /* Convert array to CSV string */
        const csvString = header + data2Csv(csvArray);

        downloadify(filename, csvString, { type: `text/${format}` });
      });
    });
  }
}
